<template>
  <div class="interview-header">
    <ProgressBar
      :value="progress"
      class="interview-header__progress"
      data-test-id="InterviewProgressBar"
    />
    <div
      class="interview-header__page-name"
      :class="{
        'interview-header__page-name--empty': !Boolean(brandImage),
      }"
    >
      <img v-if="brandImage" :src="brandImage" class="interview-header__page-name__brand-image" />
    </div>
    <div
      v-if="mediaType || imageUrl || titleHtml !== undefined"
      class="interview-header__title-container"
      :class="{ 'interview-header__title-container--align-left': !!imageUrl }"
    >
      <button
        v-if="['audio', 'video'].includes(mediaType)"
        class="interview-header__media-button"
        data-test-id="InterviewHeader_VideoOrAudioButton"
        @click="$emit('viewMedia')"
      >
        <img class="interview-header__media-icon" :src="mediaIcon" />
        <span class="sr-only">Open media preview</span>
      </button>
      <button
        v-if="mediaType === 'image' && imageUrl"
        class="interview-header__image-button"
        data-test-id="InterviewHeader_ImageButton"
        @click="$emit('viewMedia')"
      >
        <img class="interview-header__image-button-image" :src="imageUrl" />
      </button>
      <!-- eslint-disable vue/no-v-html -->
      <h1
        class="interview-header__title"
        :class="{
          'interview-header__title--narrow': ['audio', 'video'].includes(mediaType),
        }"
        v-html="titleHtml"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <h2
      v-if="subTitle.length > 0"
      class="interview-header__subtitle"
      data-test-id="InterviewHeader_Subtitle"
    >
      {{ subTitle }}
    </h2>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'

import { parseUserMarkdown } from '../../../interview/utils/parseUserMarkdown'
import playButtonIcon from '../../../question/assets/play-button.svg'
import { type MediaType } from '../../../question/models/Media'
import ProgressBar from '../../../shared/components/dumb/ProgressBar.vue'
import expandWhiteIcon from '../../assets/expand-white.svg'
import videoClipIcon from '../../assets/video-clip.svg'

export default defineComponent({
  components: {
    ProgressBar,
  },
  props: {
    titleMarkdown: {
      type: String,
      default: undefined,
    },
    subTitle: {
      type: String,
      default: '',
    },
    imageUrl: {
      type: String,
      default: '',
    },
    progress: {
      type: Number,
      default: 0,
    },
    mediaType: {
      type: String as PropType<MediaType | ''>,
      default: '',
    },
    brandImage: {
      type: [String, null],
      default: null,
    },
  },
  emits: {
    viewMedia: () => true,
  },
  setup(props) {
    const titleHtml = computed(() =>
      props.titleMarkdown === undefined ? undefined : parseUserMarkdown(props.titleMarkdown),
    )

    const mediaIcon = computed(() => {
      if (!props.mediaType) {
        return ''
      }
      const iconMap: Record<MediaType, string> = {
        video: videoClipIcon,
        mux: videoClipIcon,
        audio: playButtonIcon,
        image: expandWhiteIcon,
      }
      return iconMap[props.mediaType] ?? ''
    })

    return {
      titleHtml,
      mediaIcon,
    }
  },
})
</script>

<style lang="postcss">
.interview-header {
  &__media-button {
    align-items: center;
    align-self: flex-end;
    background-color: transparent;
    border: 1px solid var(--color-blue-grey);
    border-radius: var(--attest-border-radius);
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: 2px;
  }
  &__media-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
  &__page-name,
  &__title-container {
    border-bottom: 1px solid var(--color-blue-grey);
    text-align: center;
  }
  &__page-name {
    padding: 0 32px;
    display: grid;
    grid-template-rows: minmax(0, 60px);
    justify-content: center;
    align-items: center;

    &--empty {
      padding: 1.5rem;
      grid-template-rows: 0;
    }

    &__brand-image {
      max-height: 44px;
      max-width: 220px;
      object-fit: contain;
    }
  }
  &__title-container {
    padding: 1rem;
    min-height: 84px;
    display: flex;
    align-items: center;

    @media (min-width: 640px) {
      min-height: 120px;
    }

    &--align-left {
      text-align: left;
    }
  }
  &__subtitle {
    font-size: var(--attest-font-size-m);
    background-color: var(--color-selected);
    padding: 1em;
    color: white;
    text-align: center;
  }
  &__image-button {
    border: 0;
    background-color: transparent;
    padding: 0;
    cursor: pointer;
    height: 56px;
    width: inherit;
    position: relative;
    background-position: center;
    background-size: cover;
    margin-right: var(--attest-spacing-4);
  }
  &__image-button::after {
    content: ' ';
    background-color: var(--color-selected);
    color: white;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    position: absolute;
    right: -12px;
    bottom: -12px;
    background-image: url('../../assets/expand-white.svg');
    background-size: auto 11px;
    background-repeat: no-repeat;
    background-position: center;
  }

  &__image-button-image {
    width: 56px;
    height: 56px;
    object-fit: contain;
  }

  &__title {
    color: #000039;
    font-size: var(--attest-font-size-m);
    width: 100%;

    &--narrow {
      padding: 0 var(--attest-spacing-4);
      width: 90%;
    }
  }
  &__progress {
    border-radius: 0;
    height: 0.5rem;
    background-color: white;
  }
}
</style>
